import { useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Search, Eye, ShoppingBag, Package, Truck, XCircle, CheckCircle } from "lucide-react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import {
  getAllOrders,
  updateOrderStatus,
  selectAdminOrders,
  selectAdminOrdersLoading,
  selectAdminOrdersError,
  selectFilteredAdminOrders,
  selectPaginatedAdminOrders,
  selectAdminOrdersTotalPages,
  setSearchTerm,
  setStatusFilter,
  setCurrentPage,
  setItemsPerPage,
  selectOrderFilters,
  selectOrderPagination,
} from "@/store/slices/orderSlice"
import Loader from "@/components/loader/loader"
import { formatCurrency } from "@/utils/format"

export default function Orders() {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const orders = useAppSelector(selectAdminOrders)
  const loading = useAppSelector(selectAdminOrdersLoading)
  const error = useAppSelector(selectAdminOrdersError)
  const filters = useAppSelector(selectOrderFilters)
  const pagination = useAppSelector(selectOrderPagination)
  const filteredOrders = useAppSelector(selectFilteredAdminOrders)
  const currentOrders = useAppSelector(selectPaginatedAdminOrders)
  const totalPages = useAppSelector(selectAdminOrdersTotalPages)

  const itemsPerPage = 5

  // Read search parameter from URL on mount
  useEffect(() => {
    const searchFromUrl = searchParams.get('search')
    if (searchFromUrl) {
      dispatch(setSearchTerm(searchFromUrl))
      // Clear the search param from URL after reading it
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, dispatch, setSearchParams])

  useEffect(() => {
    dispatch(getAllOrders())
    dispatch(setItemsPerPage(itemsPerPage))
  }, [dispatch])

  const handlePageChange = (pageNumber) => {
    dispatch(setCurrentPage(pageNumber))
  }

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await dispatch(updateOrderStatus({ orderId, status: newStatus })).unwrap()
    } catch (err) {
      // Error already handled in thunk
    }
  }

  const handleSearchChange = (value) => {
    dispatch(setSearchTerm(value))
  }

  const handleStatusFilterChange = (value) => {
    // Convert "all" to empty string for the reducer (which will convert it back to "all")
    dispatch(setStatusFilter(value === "all" ? "" : value))
  }

//   const getStatusBadgeColor = (status) => {
//     switch (status) {
//       case "Delivered":
//         return "bg-green-100 text-green-700"
//       case "Shipped":
//         return "bg-blue-100 text-blue-700"
//       case "Processing":
//         return "bg-yellow-100 text-yellow-700"
//       case "Cancelled":
//         return "bg-red-100 text-red-700"
//       default:
//         return "bg-gray-100 text-gray-700"
//     }
//   }

//   const getStatusIcon = (status) => {
//     switch (status) {
//       case "Delivered":
//         return <CheckCircle className="w-4 h-4 text-green-700" />
//       case "Shipped":
//         return <Truck className="w-4 h-4 text-blue-700" />
//       case "Processing":
//         return <Package className="w-4 h-4 text-yellow-700" />
//       case "Cancelled":
//         return <XCircle className="w-4 h-4 text-red-700" />
//       default:
//         return null
//     }
//   }

  if (loading) {
    return <Loader />
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <Package className="w-16 h-16 mb-4" />
        <p className="text-lg">{error}</p>
        <Button onClick={() => dispatch(getAllOrders())} className="mt-4">
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <ShoppingBag className="w-8 h-8 text-primary" />
        <h1 className="text-3xl font-bold text-primary">Orders Management</h1>
      </div>
      <p className="text-gray-600">Manage all customer orders, view details, and update their status.</p>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search orders by ID, customer, or status..."
            className="pl-9 pr-4 py-2 rounded-md border border-input focus:ring-primary focus:border-primary"
            value={filters.searchTerm || ""}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
        <Select onValueChange={handleStatusFilterChange} value={filters.status || "all"}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="Processing">Processing</SelectItem>
            <SelectItem value="Shipped">Shipped</SelectItem>
            <SelectItem value="Delivered">Delivered</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders List Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">All Orders</CardTitle>
          <CardDescription>A comprehensive list of all customer orders.</CardDescription>
        </CardHeader>
        <CardContent>
          {currentOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order ID</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentOrders.map((order) => {
                    const orderId = order.order_id || order.id
                    // Construct customer name from first_name and last_name (backend format)
                    // or fallback to other possible field names
                    let customerName = "N/A"
                    if (order.first_name || order.last_name) {
                      customerName = [order.first_name, order.last_name].filter(Boolean).join(" ").trim() || "N/A"
                    } else if (order.customer_name) {
                      customerName = order.customer_name
                    } else if (order.customer) {
                      customerName = order.customer
                    } else if (order.user?.name) {
                      customerName = order.user.name
                    } else if (order.email) {
                      customerName = order.email
                    }
                    const orderDate = order.created_at || order.createdAt || order.date
                    const orderTotal = parseFloat(order.total || 0)
                    const orderStatus = order.status || "Processing"
                    
                    return (
                      <TableRow key={orderId}>
                        <TableCell className="font-medium">{orderId}</TableCell>
                        <TableCell>{customerName}</TableCell>
                        <TableCell>
                          {orderDate
                            ? new Date(orderDate).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "N/A"}
                        </TableCell>
                        <TableCell>{formatCurrency(orderTotal)}</TableCell>
                        <TableCell>
                          <Select
                            value={orderStatus}
                            onValueChange={(newStatus) => handleStatusChange(orderId, newStatus)}
                          >
                            <SelectTrigger className="w-[140px] h-8">
                              <SelectValue placeholder="Select Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Processing">
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4 text-yellow-700" /> Processing
                                </div>
                              </SelectItem>
                              <SelectItem value="Shipped">
                                <div className="flex items-center gap-2">
                                  <Truck className="w-4 h-4 text-blue-700" /> Shipped
                                </div>
                              </SelectItem>
                              <SelectItem value="Delivered">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-700" /> Delivered
                                </div>
                              </SelectItem>
                              <SelectItem value="Cancelled">
                                <div className="flex items-center gap-2">
                                  <XCircle className="w-4 h-4 text-red-700" /> Cancelled
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Link to={`/admin/orders/${orderId}`}>
                            <Button variant="outline" size="sm">
                              <Eye className="w-4 h-4 mr-2" /> View Details
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4" />
              <p className="text-lg">No orders found.</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className="mt-8">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                    isActive={pagination.currentPage > 1}
                  />
                </PaginationItem>
                {[...Array(totalPages)].map((_, i) => (
                  <PaginationItem key={i}>
                    <PaginationLink
                      href="#"
                      onClick={() => handlePageChange(i + 1)}
                      isActive={pagination.currentPage === i + 1}
                    >
                      {i + 1}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                    isActive={pagination.currentPage < totalPages}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
